import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { baseUrl, Endpoints } from '../../api';
import { AppThunk } from '../store';
import { smartRequest } from '../../api/index';

// define the shape of a product
// replace fields with your actual backend schema
export interface Product {
    id: number;
    name: string;
    // add more fields here
}

// shape of API response
export interface ProductListResponse {
    items: Product[];
    totalCount?: number;
}

// redux state for this slice
export interface ProductListState {
    pageNumber: number;
    items: Product[];
    status: 'idle' | 'loading' | 'failed';
    isAddingMoreItems: boolean;
    hasMore: boolean;
    currentSearch: string;
    currentCategory: string;
    isSearch: boolean;
    /** Bumped on every fresh (non-load-more) fetch; stale in-flight requests from a prior generation are ignored on arrival */
    requestGeneration: number;
}

/** API page size — load-more when a full page is returned */
export const PRODUCT_LIST_PAGE_SIZE = 60;

/**
 * Max items kept in Redux — drops oldest pages during infinite scroll.
 * Must stay large enough that the virtualized grid's total height (derived from
 * items.length) keeps growing across a normal scroll session — otherwise the
 * load-more sentinel never moves out of view and fetches fire back-to-back with
 * no further scrolling. Kept high (15 pages) so this only bounds pathological
 * sessions rather than normal browsing.
 */
export const PRODUCT_LIST_MAX_ITEMS = PRODUCT_LIST_PAGE_SIZE * 15;

// default redux state
const initialState: ProductListState = {
    items: [],
    status: 'idle',
    isAddingMoreItems: false,
    hasMore: true,
    currentSearch: '',
    currentCategory: '',
    isSearch: false,
    pageNumber: 0,
    requestGeneration: 0
};

// server expects this shape for queries
export interface ProductListParams {
    pageNumber: number;
    sortby: { columName: string; type: 'asc' | 'desc' };
    filter: {
        categoryId: number;
        parentCategoryId: number;
        countryId: number;
        regionId: number;
        cityId: number;
        fromPrice: number;
        toPrice: number;
        categories: number[];
    };
    lang: string;
    search: string;
    userId: number;
    showCurrency: string;
}

const initialParams: ProductListParams = {
    pageNumber: 1,
    sortby: { columName: 'createdDate', type: 'desc' },
    filter: {
        categoryId: 0,
        parentCategoryId: 0,
        countryId: 0,
        regionId: 0,
        cityId: 0,
        fromPrice: -1,
        toPrice: -1,
        categories: []
    },
    lang: 'en',
    search: '',
    userId: 0,
    showCurrency: ''
};

/** `generation` pins a request to the productList state it was issued against — a stale response whose
 * generation no longer matches current state (because a fresh search/filter/sort reset happened meanwhile)
 * is ignored in the reducers below instead of being merged into unrelated results. */
interface FetchProductListArg {
    params: ProductListParams;
    generation: number;
}

// No `condition` gate here: `requestGeneration` (bumped by `resetPage`, checked in the reducers below)
// is what makes duplicate/superseded requests safe. A `condition` that silently skips dispatch based on
// `status` would leave a just-bumped generation with no request left to ever resolve it, stranding
// `status` at 'loading' forever — page>1 (load-more) is separately gated by the synchronous lock in
// `fetchItems` before this is ever dispatched.
export const fetchProductList = createAsyncThunk<Product[], FetchProductListArg>(
    'productList/fetch',
    async ({ params }) => {
        const response = await smartRequest(baseUrl + Endpoints.productList, {
            method: 'POST',
            body: JSON.stringify(params)
        });

        if (!response) return [];
        return response as Product[];
    }
);

export const fetchItems =
    (data: Partial<ProductListParams>, loadMore = false): AppThunk =>
    async (dispatch, getState) => {
        const { productList, params } = getState();
        const nextPage = loadMore ? productList.pageNumber + 1 : 1;

        const merged: ProductListParams = {
            ...initialParams,
            ...params,
            ...data,
            pageNumber: nextPage,
            sortby: {
                ...initialParams.sortby,
                ...(params?.sortby ?? {}),
                ...(data?.sortby ?? {}),
                type: ((data?.sortby?.type ?? params?.sortby?.type) || 'desc') as 'asc' | 'desc'
            }
        };

        if (loadMore) {
            if (!productList.hasMore || productList.isAddingMoreItems || productList.status === 'loading') {
                return;
            }
            // Synchronous lock — pending handler runs too late for fast scroll bursts
            dispatch(setAddingMoreItems(true));
            dispatch(fetchProductList({ params: merged, generation: productList.requestGeneration }));
        } else {
            dispatch(resetPage());
            // Read the post-reset generation — resetPage() just bumped it synchronously above
            dispatch(fetchProductList({ params: merged, generation: getState().productList.requestGeneration }));
        }
    };

export const productListSlice = createSlice({
    name: 'productList',
    initialState,
    reducers: {
        setItems: (state, action: PayloadAction<Product[]>) => {
            state.items = action.payload;
        },
        setAddingMoreItems: (state, action: PayloadAction<boolean>) => {
            state.isAddingMoreItems = action.payload;
        },
        setCurrentSearch: (state, action: PayloadAction<string>) => {
            state.currentSearch = action.payload;
            state.isSearch = true;
        },
        setCurrentCategory: (state, action: PayloadAction<string>) => {
            state.currentCategory = action.payload;
            state.isSearch = false;
        },
        resetPage: (state) => {
            state.pageNumber = 0;
            state.items = [];
            state.hasMore = true;
            state.isAddingMoreItems = false;
            state.requestGeneration += 1;
        },
        incrementPage: (state) => {
            state.pageNumber += 1;
        },
        resetSearch: (state) => {
            state.currentSearch = '';
            state.isSearch = false;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchProductList.pending, (state, action) => {
                if (action.meta.arg.generation !== state.requestGeneration) return;
                const page = action.meta.arg.params.pageNumber;
                if (page > 1) {
                    state.isAddingMoreItems = true;
                } else {
                    state.status = 'loading';
                    state.isAddingMoreItems = false;
                }
            })
            .addCase(fetchProductList.fulfilled, (state, action) => {
                if (action.meta.arg.generation !== state.requestGeneration) return;
                const payload = action.payload;
                const page = action.meta.arg.params.pageNumber;

                if (page <= 1) {
                    state.items = payload;
                } else {
                    const merged = [...state.items, ...payload];
                    state.items =
                        merged.length > PRODUCT_LIST_MAX_ITEMS ? merged.slice(-PRODUCT_LIST_MAX_ITEMS) : merged;
                }

                state.pageNumber = page;
                state.hasMore = payload.length >= PRODUCT_LIST_PAGE_SIZE;
                state.status = 'idle';
                state.isAddingMoreItems = false;
            })
            .addCase(fetchProductList.rejected, (state, action) => {
                if (action.meta.arg.generation !== state.requestGeneration) return;
                const page = action.meta.arg.params.pageNumber;
                if (page > 1) {
                    state.isAddingMoreItems = false;
                } else {
                    state.status = 'failed';
                }
            });
    }
});

export const { setItems, setAddingMoreItems, setCurrentSearch, setCurrentCategory, resetPage, incrementPage, resetSearch } = productListSlice.actions;

export {
    selectProductListItems as selectItems,
    selectProductListStatus as selectStatus,
    selectIsAddMoreLoading,
    selectProductListHasMore,
    selectCurrentSearch,
    selectCurrentCategory,
    selectIsSearch as isSearch
} from '../selectors';

export default productListSlice.reducer;

// thunk wrapper to merge params with current redux params
